import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/cn'
import { formatFileSize } from '@/features/documents/utils/documentUtils'
import { toViewableBlob } from '@/lib/viewableFile'

export interface PreviewFile {
  id: string
  /** Direct link. Leave out for private files and pass `loadFile` instead. */
  url?: string
  name: string
  mimeType?: string
  size?: number
}

export interface FilePreviewModalProps {
  open: boolean
  onClose: () => void
  title: string
  /** Context line under the file name (e.g. "3 receipts" or a category label). */
  subtitle?: string
  files: PreviewFile[]
  /** File to preselect when opening; defaults to the first file. */
  initialFileId?: string
  /** Custom download handler (e.g. authenticated download endpoint). Defaults to fetching the file URL. */
  onDownload?: (file: PreviewFile) => void | Promise<void>
  /** Reads a private file through an authenticated request; used instead of `url`. */
  loadFile?: (file: PreviewFile) => Promise<Blob>
}

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

const ICON_BUTTON_CLASS =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-40'

function isImagePreviewFile(file: PreviewFile): boolean {
  return (
    (file.mimeType?.startsWith('image/') ?? false) ||
    /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i.test(file.name) ||
    /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(file.url ?? '')
  )
}

function isPdfPreviewFile(file: PreviewFile): boolean {
  return (
    file.mimeType === 'application/pdf' ||
    /\.pdf$/i.test(file.name) ||
    /\.pdf(\?|$)/i.test(file.url ?? '')
  )
}

async function fetchFromUrl(file: PreviewFile): Promise<Blob> {
  const response = await fetch(file.url ?? '')
  if (!response.ok) throw new Error(`Download failed with status ${response.status}`)
  return response.blob()
}

function saveBlob(blob: Blob, file: PreviewFile): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = file.name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/**
 * WhatsApp-style full-screen media viewer: dark overlay, top bar with file
 * info and actions, large centered preview with prev/next chevrons, and a
 * thumbnail strip along the bottom. Supports images (zoom, rotate), PDFs
 * (inline frame), and a download fallback for everything else.
 */
export function FilePreviewModal({
  open,
  onClose,
  title,
  subtitle,
  files,
  initialFileId,
  onDownload,
  loadFile,
}: FilePreviewModalProps): React.ReactElement | null {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [downloading, setDownloading] = useState(false)
  const [loadedUrls, setLoadedUrls] = useState<Record<string, string>>({})
  const [failedIds, setFailedIds] = useState<Record<string, boolean>>({})
  const loadedUrlsRef = useRef<Record<string, string>>({})

  useEffect(() => {
    if (open) setSelectedId(initialFileId ?? null)
  }, [open, initialFileId])

  const selected = useMemo(
    () => files.find((file) => file.id === selectedId) ?? files[0] ?? null,
    [files, selectedId],
  )
  const selectedIndex = selected ? files.findIndex((file) => file.id === selected.id) : -1
  const isImage = selected ? isImagePreviewFile(selected) : false
  const isPdf = selected ? isPdfPreviewFile(selected) : false
  const selectedUrl = selected && loadFile ? loadedUrls[selected.id] : selected?.url
  const selectedFailed = !!selected && !selectedUrl && !!failedIds[selected.id]

  // Private files: read the file on show, and keep it while the viewer stays open.
  useEffect(() => {
    if (!open || !loadFile || !selected || !(isImage || isPdf)) return
    if (loadedUrlsRef.current[selected.id]) return
    let cancelled = false
    loadFile(selected)
      .then((blob) => {
        if (cancelled) return
        const url = URL.createObjectURL(toViewableBlob(blob, selected))
        loadedUrlsRef.current[selected.id] = url
        setLoadedUrls((previous) => ({ ...previous, [selected.id]: url }))
      })
      .catch(() => {
        if (!cancelled) setFailedIds((previous) => ({ ...previous, [selected.id]: true }))
      })
    return () => {
      cancelled = true
    }
  }, [open, loadFile, selected, isImage, isPdf])

  // Leaving the viewer frees every file it read.
  useEffect(() => {
    if (!open) return
    const urls = loadedUrlsRef
    return () => {
      Object.values(urls.current).forEach((url) => URL.revokeObjectURL(url))
      urls.current = {}
      setLoadedUrls({})
      setFailedIds({})
    }
  }, [open])

  // Reset zoom/rotation whenever the previewed file changes.
  useEffect(() => {
    setZoom(1)
    setRotation(0)
  }, [selected?.id])

  useEffect(() => {
    if (!open) return
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      const target = event.target as HTMLElement | null
      if (target && /^(input|textarea|select)$/i.test(target.tagName)) return
      const delta = event.key === 'ArrowRight' ? 1 : -1
      const next = files[selectedIndex + delta]
      if (next) setSelectedId(next.id)
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [open, files, selectedIndex, onClose])

  if (!open) return null

  const goTo = (delta: number): void => {
    const next = files[selectedIndex + delta]
    if (next) setSelectedId(next.id)
  }

  const handleDownload = async (file: PreviewFile): Promise<void> => {
    setDownloading(true)
    try {
      if (onDownload) {
        await onDownload(file)
      } else {
        saveBlob(await (loadFile ?? fetchFromUrl)(file), file)
      }
    } catch {
      toast.error(`Could not download ${file.name}`)
    } finally {
      setDownloading(false)
    }
  }

  const metaParts = [
    subtitle,
    selected?.size ? formatFileSize(selected.size) : null,
    files.length > 1 && selectedIndex >= 0 ? `${selectedIndex + 1} of ${files.length}` : null,
  ].filter(Boolean)

  const content = (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-gray-950/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Top bar */}
      <div className="flex shrink-0 items-center gap-2 px-3 py-2.5 sm:px-4">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{selected?.name ?? title}</p>
          {metaParts.length > 0 ? (
            <p className="truncate text-xs text-gray-400">{metaParts.join(' · ')}</p>
          ) : null}
        </div>

        {isImage ? (
          <div className="hidden items-center gap-1 sm:flex">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Zoom out"
              title="Zoom out"
              className={ICON_BUTTON_CLASS}
            >
              <ZoomOut className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                setZoom(1)
                setRotation(0)
              }}
              title="Reset zoom"
              className="min-w-12 rounded px-1 text-center text-xs tabular-nums text-gray-300 transition-colors hover:text-white"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Zoom in"
              title="Zoom in"
              className={ICON_BUTTON_CLASS}
            >
              <ZoomIn className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              aria-label="Rotate"
              title="Rotate"
              className={ICON_BUTTON_CLASS}
            >
              <RotateCw className="h-4 w-4" aria-hidden />
            </button>
            <span className="mx-1 h-5 w-px bg-white/15" aria-hidden />
          </div>
        ) : null}

        {selected ? (
          <>
            {selectedUrl ? (
            <a
              href={selectedUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${selected.name} in a new tab`}
              title="Open in new tab"
              className={ICON_BUTTON_CLASS}
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
            ) : null}
            <button
              type="button"
              onClick={() => void handleDownload(selected)}
              disabled={downloading}
              aria-label={`Download ${selected.name}`}
              title="Download"
              className={cn(ICON_BUTTON_CLASS, downloading && 'w-auto gap-1.5 px-3')}
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  <span className="text-xs font-medium text-white">Downloading…</span>
                </>
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
            </button>
          </>
        ) : null}

        <span className="mx-1 h-5 w-px bg-white/15" aria-hidden />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          className={ICON_BUTTON_CLASS}
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {/* Stage — clicking the empty area closes, like WhatsApp */}
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-14 py-2 sm:px-16"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose()
        }}
      >
        {selected && selectedFailed ? (
          <div className="max-w-sm text-center">
            <FileText className="mx-auto mb-3 h-12 w-12 text-gray-500" aria-hidden />
            <p className="text-sm font-medium text-white">Couldn&apos;t open this file.</p>
            <p className="mt-1 text-xs text-gray-400">
              You may not have access to it, or it may have been removed.
            </p>
          </div>
        ) : selected && (isImage || isPdf) && !selectedUrl ? (
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-label="Loading file" />
        ) : selected && isImage ? (
          <img
            src={selectedUrl}
            alt={selected.name}
            style={{ transform: `rotate(${rotation}deg) scale(${zoom})` }}
            className="max-h-full max-w-full select-none object-contain transition-transform"
            draggable={false}
          />
        ) : selected && isPdf ? (
          <iframe
            src={selectedUrl}
            title={selected.name}
            className="h-full w-full max-w-5xl rounded-md border-0 bg-white"
          />
        ) : selected ? (
          <div className="max-w-sm text-center">
            <FileText className="mx-auto mb-3 h-12 w-12 text-gray-500" aria-hidden />
            <p className="text-sm font-medium text-white">
              Preview is not available for this file type.
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Images and PDFs can be viewed here. Download the file to open it.
            </p>
            <button
              type="button"
              onClick={() => void handleDownload(selected)}
              disabled={downloading}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20 disabled:pointer-events-none disabled:opacity-40"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
              Download
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No files to preview.</p>
        )}

        {selectedIndex > 0 ? (
          <button
            type="button"
            onClick={() => goTo(-1)}
            aria-label="Previous file"
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20 sm:left-4"
          >
            <ChevronLeft className="h-6 w-6" aria-hidden />
          </button>
        ) : null}
        {selectedIndex >= 0 && selectedIndex < files.length - 1 ? (
          <button
            type="button"
            onClick={() => goTo(1)}
            aria-label="Next file"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20 sm:right-4"
          >
            <ChevronRight className="h-6 w-6" aria-hidden />
          </button>
        ) : null}
      </div>

      {/* Thumbnail strip */}
      {files.length > 1 ? (
        <div className="shrink-0 overflow-x-auto px-4 py-3">
          <div className="mx-auto flex w-max items-center gap-2">
            {files.map((file) => {
              const isSelected = selected?.id === file.id
              return (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => setSelectedId(file.id)}
                  aria-label={`Preview ${file.name}`}
                  title={file.name}
                  className={cn(
                    'h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 transition-all',
                    isSelected
                      ? 'border-white opacity-100'
                      : 'border-transparent opacity-50 hover:opacity-90',
                  )}
                >
                  {isImagePreviewFile(file) && (loadFile ? loadedUrls[file.id] : file.url) ? (
                    <img
                      src={loadFile ? loadedUrls[file.id] : file.url}
                      alt=""
                      loading="lazy"
                      draggable={false}
                      className="h-full w-full select-none object-cover"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center bg-white/10">
                      <FileText className="h-5 w-5 text-gray-300" aria-hidden />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )

  return createPortal(content, document.body)
}

FilePreviewModal.displayName = 'FilePreviewModal'
